(ns minorodata.components.dependency-graph.sidebar
  "Collapsible right-hand sidebar for the dependency-graph page. Surfaces the same
   global \"failed today\" extracts and transforms that the app sidebar badges show
   (`:statuses.subs/failed-today` / `:transform.subs/failed-statuses-today`),
   so failures are visible without leaving the graph. A single collapsible
   \"Failures\" section lists failing extracts and transforms together, each row
   tagged with its entity-type icon (the same icon/colour convention as the
   global-search results), with tabs to filter the list to a single type.

   The row renderers are copied (rather than reused) from `minorodata.components.sidebar.core`
   on purpose: that namespace requires `minorodata.components.core`, which in turn requires the
   dependency-graph page — requiring it here would be circular."
  (:require
   ["date-fns" :refer [formatDistance isAfter subDays]]
   ["lucide-react" :refer [Activity ArrowDown ArrowUp ChartNoAxesGanttIcon ChevronDown ChevronRight CircleCheck Clock
                           Crosshair DatabaseIcon EllipsisVertical GitGraphIcon LayoutList PlayCircle ScrollText
                           Settings Sparkles TerminalSquare]]
   ["react" :as react]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
   [minorodata.components.spinner.core :refer [spinner-lucide]]
   [re-posh.core :as re-posh]
   [ui.dropdown-menu :as dropdown-menu]
   [ui.status :refer [status-pill]]
   [ui.status-vocab :refer [normalize-status]]
   [ui.tabs :as tabs]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))


(defn- icon-wrapper
  "Rounded, colored box around a row/tab icon — mirrors the global-search result
  icon (minorodata.components.sidebar.global-search/icon-wrapper). Color is
  supplied per entity type via `:class`; `:box`/`:icon-size` tune the size."
  [{:keys [class box icon-size]} icon]
  [:div {:class (clsx "flex items-center justify-center rounded-md shrink-0" (or box "size-7") class)}
   [:> icon {:size (or icon-size 16)}]])

;; Each failure row is in the global-search result style: a clickable body (entity
;; icon + name + muted location subtitle) opens the entity in a new anchored tab
;; (subpipeline viewer) and centers it — see `:dependency-graph.events/reveal-node`
;; — while the kebab, split off by a small gap as its own highlighted zone, opens a
;; dropdown of the bottom-pane / cross-feature actions.
(def ^:private row-class "group flex min-w-0 items-stretch rounded-md")
(def ^:private row-body-class
  "flex min-w-0 flex-1 items-center gap-2 rounded-md p-2 text-sm text-left cursor-pointer transition-colors hover:bg-kleene-hover-background")
;; The kebab collapses to zero width until the row is hovered (or its menu is
;; open / it has keyboard focus), so at rest the row body fills the full width and
;; touches the right edge. On reveal it expands (and gains a small gap from the
;; body) into its own highlighted zone — see `row-class`'s `group`.
(def ^:private kebab-class
  ["shrink-0 flex items-center justify-center rounded-md text-kleene-text-secondary"
   "w-0 overflow-hidden opacity-0 transition-all"
   "group-hover:w-10 group-hover:ml-1 group-hover:opacity-100"
   "focus-visible:w-10 focus-visible:ml-1 focus-visible:opacity-100"
   "hover:bg-kleene-hover-background"
   "data-[state=open]:w-10 data-[state=open]:ml-1 data-[state=open]:opacity-100 data-[state=open]:bg-kleene-hover-background"])

(defn- relative-time
  "Human \"5 minutes ago\" for an ISO timestamp; nil if unparseable/blank."
  [iso]
  (when (not-empty iso)
    (let [date (js/Date. iso)]
      (when-not (js/isNaN (.getTime date))
        (str (formatDistance (js/Date.) date) " ago")))))

(defn- subtitle-line
  "Muted subtitle row showing the relative timestamp; nil when unparseable/blank."
  [iso]
  (when-let [text (relative-time iso)]
    [:span {:class "truncate text-xs leading-3 text-kleene-dark-05"} text]))

;; ---------------------------------------------------------------------------
;; Row dropdown menu
;;
;; The same set of cross-feature actions is offered from every row — the failure
;; rows and the recent-runs feed alike — so the menu content is built once per
;; entity type and reused. The only thing that varies is the KAI prompt: a failed
;; run offers "Debug with KAI" (seeded with a debugging prompt), any other run
;; offers a neutral "Ask KAI". KAI only supports transforms (see
;; `:ai-chat/set-context`), so it's omitted from the extract menu.

(defn- row-menu
  "Kebab trigger + the supplied `dropdown-menu-content`. The trigger is hidden
   until the row (`row-class` `group`) is hovered — see `kebab-class`."
  [content]
  [dropdown-menu/dropdown-menu-root
   [dropdown-menu/dropdown-menu-trigger {:title "More actions" :class kebab-class}
    [:> EllipsisVertical {:size 16}]]
   content])

(defn- kai-menu-item [{:keys [context name failure?]}]
  [dropdown-menu/dropdown-menu-item
   {:class    "gap-2"
    :on-click #(do (dispatch [:ai-chat/set-context context])
                   (dispatch [:ai-chat/set-draft
                              (if failure?
                                (str "Debug why the transform \"" name "\" failed?")
                                (str "Tell me about the transform \"" name "\""))])
                   (dispatch [:modals.events/toggle-modal "ai-modal" true]))}
   [:> Sparkles {:size 14}] (if failure? "Debug with KAI" "Ask KAI")])

;; Run a transform (optionally pulling in its upstream / downstream dependencies),
;; mirroring the graph node context-menu Run action. We fetch the single transform
;; first so its current version is loaded, then run the latest version by master id
;; — same two-step the bleene graph menu uses (`bleene.dag.context-menu`); inlined
;; here rather than required to keep this namespace free of graph-page cycles.
(defn- launch-transform! [transform-id group-id option master-id]
  (dispatch [:transform.api/fetch-single-transform
             {:transform-id  transform-id
              :group-id      group-id
              :add-to-panel? false
              :on-finish     #(dispatch [:transform.api/get-latest-version-by-master-id-and-run
                                         {:type             option
                                          :master-id        master-id
                                          :old-transform-id transform-id}])}]))

(defn- run-menu-item
  "The transform-row \"Run\" entry: a submenu of single / upstream / downstream when
   the user may run transforms, otherwise a disabled item (matching the graph menu)."
  [{:keys [transform-id group-id master-id]}]
  (let [can-run? (or @(re-posh/subscribe [:users.subs/has-permissions? [:connections+transforms/write]])
                     @(re-posh/subscribe [:users.subs/has-permissions? [:transforms/write]]))
        run!     (fn [option] (launch-transform! transform-id group-id option master-id))]
    (if can-run?
      [dropdown-menu/dropdown-menu-sub
       {:content [:<>
                  [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click #(run! :single)} "Only this one"]
                  [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click #(run! :upstream)} "Upstream"]
                  [dropdown-menu/dropdown-menu-item {:class "gap-2" :on-click #(run! :downstream)} "Downstream"]]}
       [:span {:class "flex w-full items-center gap-2"}
        [:> PlayCircle {:size 14}]
        [:span {:class "flex-1"} "Run"]
        [:> ChevronRight {:size 14}]]]
      [dropdown-menu/dropdown-menu-item {:class "gap-2" :disabled true}
       [:> PlayCircle {:size 14}] "Run"])))

(defn- transform-menu-content
  "Dropdown content for a transform row. `failure?` switches the KAI item.
   Permission gating mirrors the graph node menu / classic bleene graph: write
   perms gate SQL Console (Run is gated inside `run-menu-item`), `:logs/read`
   gates Logs. The KAI context is a bare `{:master-id :group-id :name}` map (same
   as the graph node menu; `:ai-chat/set-context` normalises key namespaces)."
  [{:keys [name master-id transform-id group-id failure?]}]
  (let [can-write?     (or @(re-posh/subscribe [:users.subs/has-permissions? [:connections+transforms/write]])
                           @(re-posh/subscribe [:users.subs/has-permissions? [:transforms/write]]))
        can-read-logs? @(re-posh/subscribe [:users.subs/has-permissions? :logs/read])
        timeline?      @(re-posh/subscribe [:unleash/is-enabled "fe.execution-timeline"])]
    [dropdown-menu/dropdown-menu-content {:align "end" :hide-arrow true}
     [dropdown-menu/dropdown-menu-label "Actions"]
     [run-menu-item {:transform-id transform-id :group-id group-id :master-id master-id}]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :on-click #(dispatch [:dependency-graph.events/focus-node-on-dag {:transform-id transform-id}])}
      [:> Crosshair {:size 14}] "Focus"]
     [kai-menu-item {:context {:master-id master-id :group-id group-id :name name}
                     :name name :failure? failure?}]

     [dropdown-menu/dropdown-menu-label "Preview"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :disabled (not can-read-logs?)
       :on-click #(dispatch [:dependency-graph.events/open-logs
                             {:name name :filter {:term transform-id}}])}
      [:> ScrollText {:size 14}] "Logs"]
     (when timeline?
       [dropdown-menu/dropdown-menu-item
        {:class    "gap-2"
         :on-click #(dispatch [:dependency-graph.events/open-timeline
                               {:master-id master-id :name name :source "summary-panel"}])}
        [:> ChartNoAxesGanttIcon {:size 14}] "Execution timeline"])
     [dropdown-menu/dropdown-menu-label "Open in"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :on-click #(dispatch [:app.events/navigate :transform-details
                             {:group-id group-id :transform-id transform-id}])}
      [:> Settings {:size 14}] "Settings"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :disabled (not can-write?)
       :on-click #(dispatch [:sql-console-modal.events/open-transform transform-id])}
      [:> TerminalSquare {:size 14}] "SQL Console"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :on-click #(dispatch [:pipeline-editor.events/navigate-and-open-tab
                             {:id master-id :group-id group-id :type "transform" :name name}])}
      [:> GitGraphIcon {:size 14}] "Pipeline editor"]]))

(defn- extract-menu-content
  "Dropdown content for an extract row. `:logs/read` gates Logs (mirroring the
   graph node menu / classic bleene graph). Logs scope by the extract name term —
   the task-definition-name (used for a tighter `:source` filter) isn't resolved
   client-side here, so we fall back to the name term."
  [{:keys [name extract-id source-id]}]
  (let [can-read-logs? @(re-posh/subscribe [:users.subs/has-permissions? :logs/read])]
    [dropdown-menu/dropdown-menu-content {:align "end" :hide-arrow true}
     [dropdown-menu/dropdown-menu-label "Graph"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :on-click #(dispatch [:dependency-graph.events/focus-node-on-dag {:extract-id extract-id}])}
      [:> Crosshair {:size 14}] "Focus"]
     [dropdown-menu/dropdown-menu-label "Preview"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :disabled (not can-read-logs?)
       :on-click #(dispatch [:dependency-graph.events/open-logs
                             {:name name :filter {:term name}}])}
      [:> ScrollText {:size 14}] "Logs"]
     [dropdown-menu/dropdown-menu-label "Open in"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :on-click #(dispatch [:app.events/navigate :extract-details
                             {:source-id source-id :extract-id extract-id}])}
      [:> Settings {:size 14}] "Settings"]
     [dropdown-menu/dropdown-menu-item
      {:class    "gap-2"
       :on-click #(dispatch [:pipeline-editor.events/navigate-and-open-tab
                             {:id extract-id :group-id source-id :type "extract" :name name}])}
      [:> GitGraphIcon {:size 14}] "Pipeline editor"]]))

;; Names/menu ids are resolved by the caller from the shared indexes
;; (`:dependency-graph.subs/transform-index` / `extract-name-index`) and passed in,
;; so a row does no per-entity fetch or `[:transform/pull]`/`[:extract/pull]` sub of
;; its own. `name` is nil until those indexes load — show a "Loading…" placeholder.
(defn failed-extract [{:keys [extract-id source-id created-at name]}]
  [:div {:class row-class}
   [:button {:class    row-body-class
             :title    "Open in new tab"
             :on-click #(dispatch [:dependency-graph.events/reveal-node {:extract-id extract-id}])}
    [icon-wrapper {:class "bg-extracts/20 text-extracts" :box "size-6"} DatabaseIcon]
    [:div {:class "min-w-0 flex flex-col gap-1"}
     [:span {:class "truncate text-xs font-medium leading-3 text-kleene-text"}
      (if (not-empty name) name "Loading…")]
     (subtitle-line created-at)]]
   [row-menu [extract-menu-content {:name name :extract-id extract-id :source-id source-id}]]])

(defn failed-transform [{:keys [transform-id group-id finished-at started-at created-at name master-id]}]
  [:div {:class row-class}
   [:button {:class    row-body-class
             :title    "Open in new tab"
             :on-click #(dispatch [:dependency-graph.events/reveal-node {:transform-id transform-id}])}
    [icon-wrapper {:class "bg-transforms/20 text-transforms" :box "size-6"} LayoutList]
    [:div {:class "min-w-0 flex flex-col gap-1"}
     [:span {:class "truncate text-xs font-medium leading-3 text-kleene-text"}
      (if (not-empty name) name "Loading…")]
     (subtitle-line (or finished-at started-at created-at))]]
   [row-menu [transform-menu-content {:name name :master-id master-id
                                      :transform-id transform-id :group-id group-id :failure? true}]]])


;; ---------------------------------------------------------------------------
;; Recent runs + 24h health
;;
;; The graph already fetches the full (not fail-only) status lists for both
;; entity types — extracts into the shared status store (`:statuses.subs/all`) and
;; transforms into `:transform-status-list/statuses`. Each status carries a
;; timestamp and a status string, so we can surface a unified "recent runs"
;; feed and a 24-hour success/failure summary without any extra API calls.

(defn- rate-color
  "Threshold-based health colour for the success-rate ring/number: green when
   healthy, amber when degraded, red when poor, muted grey when unknown (no
   completed runs to compute a rate from)."
  [rate]
  (cond
    (nil? rate)   "text-kleene-text-secondary"
    (>= rate 95)  "text-kleene-text-success"
    (>= rate 80)  "text-kleene-text-warning"
    :else         "text-kleene-text-error"))

(defn- success-ring
  "Circular success-rate gauge. The track + progress arc are drawn with
   `currentColor` so they inherit the threshold colour from a `text-*` class
   (Tailwind reliably emits text utilities for theme colours; `stroke-*` for
   custom tokens is less certain). `rate` nil renders an empty grey ring."
  [rate]
  (let [size 72 stroke 7
        r (/ (- size stroke) 2)
        circ (* 2 js/Math.PI r)
        color (rate-color rate)]
    [:div {:class "relative shrink-0" :style {:width size :height size}}
     [:svg {:width size :height size :class "-rotate-90"}
      [:circle {:cx (/ size 2) :cy (/ size 2) :r r :fill "none" :stroke-width stroke
                :stroke "currentColor" :class "text-kleene-text/10"}]
      (when rate
        [:circle {:cx (/ size 2) :cy (/ size 2) :r r :fill "none" :stroke-width stroke
                  :stroke "currentColor" :stroke-linecap "round"
                  :stroke-dasharray circ
                  :stroke-dashoffset (* circ (- 1 (/ rate 100)))
                  :class (clsx "transition-all" color)}])]
     [:div {:class "absolute inset-0 flex items-center justify-center"}
      [:span {:class (clsx "text-base font-semibold leading-none" color)}
       (if rate (str rate "%") "—")]]]))

(defn- delta-badge
  "Tiny up/down change vs. the prior 24h. `good-up?` decides which direction is
   coloured green (more successes good; more failures bad). Hidden when flat."
  [delta good-up?]
  (when (and delta (not (zero? delta)))
    (let [up? (pos? delta)]
      [:span {:class (clsx "inline-flex items-center gap-0.5 text-xs tabular-nums"
                           (if (= up? good-up?) "text-kleene-text-success" "text-kleene-text-error"))}
       [:> (if up? ArrowUp ArrowDown) {:size 11}]
       (js/Math.abs delta)])))

(defn- outcome-row
  "One line of the 24h health block: a coloured status dot, the count, its label
   and a delta-vs-yesterday badge."
  [{:keys [label value variant delta good-up?]}]
  [:div {:class "flex items-center gap-2"}
   [:span {:class (clsx "size-2 shrink-0 rounded-full"
                        (case variant
                          :success "bg-kleene-text-success"
                          :failure "bg-kleene-text-error"
                          "bg-kleene-text-secondary"))}]
   [:span {:class "text-sm font-semibold tabular-nums text-kleene-text"} value]
   [:span {:class "min-w-0 flex-1 truncate text-xs text-kleene-text-secondary"} label]
   [delta-badge delta good-up?]])

(defn- empty-state
  "Centred muted icon + line, used for the calm/idle section bodies."
  [icon text]
  [:div {:class "flex flex-col items-center gap-2 py-4 text-center text-kleene-text-secondary"}
   [:> icon {:size 20 :class "opacity-60"}]
   [:span {:class "text-sm"} text]])

;; A recent-run row: same icon + name + subtitle convention as the failure rows,
;; but with a relative-time subtitle and a status pill (success rows included).
;; Clicking opens the entity in a new anchored tab, like the failure rows.
;; `name` (and, for transforms, `master-id`) are resolved by the caller from the
;; shared indexes and carried on the run map — no per-row pull/fetch.
(defn recent-run-row [{:keys [kind id group-id status at name master-id]}]
  (let [failure? (= :failed status)
        {:keys [reveal icon icon-class menu]}
        (case kind
          :extract   {:reveal     {:extract-id id}
                      :icon       DatabaseIcon
                      :icon-class "bg-extracts/20 text-extracts"
                      :menu       [extract-menu-content {:name name :extract-id id :source-id group-id}]}
          :transform {:reveal     {:transform-id id}
                      :icon       LayoutList
                      :icon-class "bg-transforms/20 text-transforms"
                      :menu       [transform-menu-content {:name name :master-id master-id
                                                           :transform-id id :group-id group-id :failure? failure?}]})]
    [:div {:class row-class}
     [:button {:class    row-body-class
               :title    "Open in new tab"
               :on-click #(dispatch [:dependency-graph.events/reveal-node reveal])}
      [icon-wrapper {:class icon-class :box "size-6"} icon]
      [:div {:class "min-w-0 flex flex-1 flex-col gap-1"}
       [:span {:class "truncate text-xs font-medium leading-3 text-kleene-text"}
        (if (not-empty name) name "Loading…")]
       (subtitle-line at)]
      [status-pill status]]
     [row-menu menu]]))


(defn- section
  "A collapsible section with a title + count header and a body. `count-variant`
   colours the count badge (`:error` red, `:warning` amber, neutral otherwise);
   `accessory` renders next to the title (e.g. a live pulse dot)."
  [{:keys [title count count-variant accessory open? on-toggle class]} body]
  [:div {:class (str "flex flex-col gap-3 " class)}
   [:button {:class    "flex w-full items-center justify-between text-left group"
             :on-click on-toggle}
    [:div {:class "flex min-w-0 items-center gap-2"}
     [typography/h4 title]
     (when (pos? count)
       [:span {:class (clsx "px-1.5 shrink-0 h-5 flex items-center justify-center rounded text-xs"
                            (case count-variant
                              :error   "bg-kleene-text-error/30 text-kleene-text-error"
                              :warning "bg-kleene-text-warning/20 text-kleene-text-warning"
                              "bg-kleene-text/10 text-kleene-text-secondary"))}
        count])
     accessory]
    [:> ChevronDown {:size  18
                     :class (str "shrink-0 text-kleene-text-secondary transition-transform "
                                 (when open? "rotate-180"))}]]
   (when open? body)])


;; Type-filter tabs. `:all` shows everything; the others scope to one entity type.
;; Each tab's `:value` doubles as its `tab-icon` type (brand-coloured icon); the
;; count is supplied per-tab at render time.
(def ^:private filter-tabs
  [{:value "all"       :label "All"}
   {:value "extract"   :label "Extracts"}
   {:value "transform" :label "Transforms"}])

(defn- type-tabs
  "Single-select underlined tabs scoping the failure list to one entity type (or
  `All`). Each tab shows its type icon on the left and its failure count on the
  right. `counts` is a map of tab value -> failure count. Mirrors the
  global-search type tabs."
  [{:keys [selected on-select counts]}]
  [tabs/tabs-root {:value           selected
                   :on-value-change on-select}
   [tabs/tabs-list {:variant :underlined :size :small :full-width true}
    (for [{:keys [value label]} filter-tabs]
      ^{:key value}
      [tabs/tabs-trigger {:value   value
                          :size    :small
                          :variant :underlined
                          :class   "gap-1.5"}
       [:span {:class "flex min-w-0 items-center gap-1.5"}
        [tab-icon {:type value :size 14}]
        [:span {:class "truncate"} label]]
       [:span {:class "rounded bg-kleene-text/10 px-1 text-kleene-text-secondary"}
        (get counts value 0)]])]])


;; The number of failure rows rendered at once. Names come from in-memory indexes
;; (no per-row fetch), but a very large failure list is still a lot of DOM — cap it
;; and surface the overflow count so the list stays responsive.
(def ^:private max-failure-rows 50)

;; Statuses are each entity's latest, however old; without a cutoff a run that
;; never reported back sat in "Running now" for months.
(def ^:private stats-days 5)

(defn- within-stats-window?
  "Whether a status is from the last `stats-days`; undated ones are not."
  [cutoff {:keys [finished-at started-at created-at]}]
  (let [at (or finished-at started-at created-at)
        d  (when (not-empty at) (js/Date. at))]
    (boolean (and d (not (js/isNaN (.getTime d))) (isAfter d cutoff)))))

(defn sidebar' []
  (let [[open? set-open]      (react/useState true)
        [running-open? set-running-open] (react/useState true)
        [recent-open? set-recent-open] (react/useState true)
        [type-filter set-filter] (react/useState "all")

        recent-status?      (partial within-stats-window? (subDays (js/Date.) stats-days))

        ;; Extracts are limited to those present in the general "Pipelines" graph
        ;; (see :dependency-graph.subs/failing-graph-extracts); transforms aren't.
        failing-extracts    (filter recent-status? @(re-posh/subscribe [:dependency-graph.subs/failing-graph-extracts]))
        extracts-count      (count failing-extracts)
        failing-transforms  (filter recent-status? @(re-posh/subscribe [:transform.subs/failed-statuses]))
        transforms-count    (count failing-transforms)

        ;; Full (not fail-only) status lists, already fetched for the graph — used
        ;; for the 24h health summary and the recent-runs feed below.
        extract-statuses    (filter recent-status? @(re-posh/subscribe [:statuses.subs/all :extract]))
        transform-statuses  (filter recent-status? @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses]))

        ;; Names + menu ids are resolved from data already in the store — the
        ;; transforms bulk-loaded by `fetch-transforms` (transform-index) and the
        ;; extract names carried on the graph nodes (extract-name-index) — so the
        ;; rows need no per-entity fetch.
        transform-index     @(re-posh/subscribe [:dependency-graph.subs/transform-index])
        extract-names       @(re-posh/subscribe [:dependency-graph.subs/extract-name-index])

        ;; The status fetches populate the failure / recent / running lists, so spin
        ;; the section placeholders while either is in flight.
        loading?    (or @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-transform-statuses])
                        @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-extract-statuses-for-graph]))
        total-count (+ (or extracts-count 0) (or transforms-count 0))
        ;; Single list of failures — each item is tagged with its kind so the row
        ;; loop can pick the right renderer (and key namespace). Sorted so the most
        ;; recent failures sit at the top (transforms expose finish/start times,
        ;; extracts only a created-at); undated rows fall to the end.
        failures    (->> (concat
                          (map (fn [e] (assoc e :kind :extract
                                              :name (get extract-names (:extract-id e))))
                               failing-extracts)
                          (map (fn [t] (let [{:keys [name master-id]} (get transform-index (:transform-id t))]
                                         (assoc t :kind :transform :name name :master-id master-id)))
                               failing-transforms))
                         (sort-by #(or (:finished-at %) (:started-at %) (:created-at %) "")
                                  #(compare %2 %1)))
        visible     (case type-filter
                      "extract"   (filter #(= :extract (:kind %)) failures)
                      "transform" (filter #(= :transform (:kind %)) failures)
                      failures)

        ;; Unified run feed across both entity types. Transforms expose
        ;; start/finish timestamps; extracts only a created-at. We sort by the
        ;; most recent timestamp so the freshest runs float to the top.
        runs        (concat
                     (map (fn [s] {:kind :extract :id (:extract-id s) :group-id (:source-id s)
                                   :status (normalize-status (:status s))
                                   :at (:created-at s)
                                   :name (get extract-names (:extract-id s))}) extract-statuses)
                     (map (fn [s] (let [{:keys [name master-id]} (get transform-index (:transform-id s))]
                                    {:kind :transform :id (:transform-id s) :group-id (:group-id s)
                                     :status (normalize-status (:status s))
                                     :at (or (:finished-at s) (:started-at s) (:created-at s))
                                     :name name :master-id master-id})) transform-statuses))
        ;; Recent = finished runs only; in-flight/queued live in "Running now" above.
        recent      (->> runs
                         (remove #(#{:running :queued} (:status %)))
                         (filter :at) (sort-by :at) reverse (take 8))

        ;; In-flight + queued runs — surfaced at the top so an operator watching
        ;; the graph can see what is executing or waiting to run right now.
        ;; Running ahead of queued, then freshest first within each.
        active        (->> runs
                           (filter #(#{:running :queued} (:status %)))
                           (filter :at)
                           (sort-by (juxt #(if (= :running (:status %)) 0 1) #(or (:at %) ""))
                                    #(compare %2 %1)))
        active-count  (count active)

        ;; A run's timestamp parsed to a Date, or nil if blank/unparseable.
        run-date    (fn [{:keys [at]}]
                      (when (not-empty at)
                        (let [d (js/Date. at)] (when-not (js/isNaN (.getTime d)) d))))
        ;; Count succeeded/failed in a [from, to) window (`to` nil = up to now).
        window-tally (fn [from to]
                       (let [in? (fn [r] (when-let [d (run-date r)]
                                           (and (isAfter d from) (or (nil? to) (not (isAfter d to))))))
                             ws  (filter in? runs)]
                         {:succeeded (count (filter #(= :succeeded (:status %)) ws))
                          :failed    (count (filter #(= :failed (:status %)) ws))}))

        ;; 24h health: succeeded / failed over the last day, plus the prior day so
        ;; each count can show a delta. Success rate is over completed runs only.
        day-ago     (subDays (js/Date.) 1)
        two-days-ago (subDays (js/Date.) 2)
        today        (window-tally day-ago nil)
        yesterday    (window-tally two-days-ago day-ago)
        succeeded   (:succeeded today)
        failed      (:failed today)
        completed   (+ succeeded failed)
        success-rate (when (pos? completed) (js/Math.round (* 100 (/ succeeded completed))))
        today-total  (+ completed
                        (count (filter #(and (run-date %)
                                             (not (#{:succeeded :failed} (:status %)))
                                             (isAfter (run-date %) day-ago)) runs)))]

    ;; No fetch on mount: `:dependency-graph.events/init` already pulls both status
    ;; lists, and this pane renders nowhere but that page, so it cannot mount without
    ;; init having run. Fetching again sent a second, racing GET for each.

    [:div {:class "flex h-full flex-col border-l bg-kleene-background overflow-hidden"}
     [:div {:class "px-4 py-4 shrink-0"}
      [typography/h3 {:class "whitespace-nowrap"} "Pipeline execution summary"]]

     [:div {:class "min-h-0 flex-1 overflow-y-auto px-4 pt-2 pb-kai flex flex-col gap-6"}

      ;; 24-hour health summary — a success-rate ring beside succeeded / failed
      ;; counts, each with a delta vs. the prior 24 hours.
      [:div {:class "flex flex-col gap-3 border-t border-kleene-border"}
       [typography/h4 "Last 24 hours"]
       [:div {:class "flex items-center gap-4"}
        [:div {:class "flex flex-col items-center gap-1"}
         [success-ring success-rate]
         [:span {:class "text-xs text-kleene-text-secondary"} "success"]]
        [:div {:class "flex min-w-0 flex-1 flex-col gap-1.5"}
         [outcome-row {:label "Succeeded" :value succeeded :variant :success
                       :delta (- succeeded (:succeeded yesterday)) :good-up? true}]
         [outcome-row {:label "Failed" :value failed :variant :failure
                       :delta (- failed (:failed yesterday)) :good-up? false}]
         [:span {:class "text-xs text-kleene-text-secondary"} (str today-total " runs total")]]]]

      ;; Running now — in-flight + queued executions, surfaced first. A pulsing
      ;; dot in the header signals live activity; idle shows a calm empty state.
      [section {:title         "Running now"
                :count         active-count
                :count-variant :warning
                :accessory     (when (pos? active-count)
                                 [:span {:class "relative flex size-2"}
                                  [:span {:class "absolute inline-flex h-full w-full animate-ping rounded-full bg-kleene-text-warning opacity-75"}]
                                  [:span {:class "relative inline-flex size-2 rounded-full bg-kleene-text-warning"}]])
                :open?         running-open?
                :on-toggle     #(set-running-open (not running-open?))}
       (cond
         (and loading? (empty? active))
         [:div {:class "w-full flex items-center justify-center gap-2 py-2 text-kleene-text-secondary"}
          [spinner-lucide] "Loading..."]

         (empty? active)
         [empty-state Activity "Nothing to run right now."]

         :else
         [:div {:class "flex flex-col gap-0.5"}
          (for [{:keys [kind id status] :as run} active]
            ^{:key (str "active-" (name kind) "-" id "-" (name status))}
            [recent-run-row run])])]

      [section {:title     "Recent executions"
                :count     0
                :open?     recent-open?
                :on-toggle #(set-recent-open (not recent-open?))}
       (cond
         (and loading? (empty? recent))
         [:div {:class "w-full flex items-center justify-center gap-2 py-2 text-kleene-text-secondary"}
          [spinner-lucide] "Loading..."]

         (empty? recent)
         [empty-state Clock "No runs to show yet."]

         :else
         [:div {:class "flex flex-col gap-0.5"}
          (for [{:keys [kind id] :as run} recent]
            ^{:key (str "run-" (name kind) "-" id "-" (:at run))}
            [recent-run-row run])])]

      [section {:title     "Failed executions"
                :count     total-count
                :count-variant :error
                :open?     open?
                :on-toggle #(set-open (not open?))}
       [:div {:class "flex flex-col gap-3"}
        [type-tabs {:selected  type-filter
                    :on-select set-filter
                    :counts    {"all"       total-count
                                "extract"   (or extracts-count 0)
                                "transform" (or transforms-count 0)}}]
        (cond
          (and loading? (empty? failures))
          [:div {:class "w-full flex items-center justify-center gap-2 py-2 text-kleene-text-secondary"}
           [spinner-lucide] "Loading..."]

          (empty? visible)
          [empty-state CircleCheck "Good news! Nothing has failed."]

          :else
          [:div {:class "flex flex-col gap-0.5"}
           (for [{:keys [kind] :as failure} (take max-failure-rows visible)]
             (case kind
               :extract
               ^{:key (str "extract-" (:extract-id failure))}
               [failed-extract failure]

               :transform
               ^{:key (str "transform-" (:transform-id failure))}
               [failed-transform failure]))
           (when (> (count visible) max-failure-rows)
             [:div {:class "px-2 py-1 text-xs text-kleene-text-secondary"}
              (str "Showing " max-failure-rows " of " (count visible) " — filter by type to narrow.")])])]]]]))

(defn sidebar []
  [:f> sidebar'])
